import { isNil } from '@wistia/type-guards';
import { defaultTimeZone, defaultLocales } from './constants';
import { isInvalidDate } from './isInvalidDate';

/**
 * Converts a Date object into a date and time string.
 *
 * e.g. Jun 3, 2021, 11:52 AM
 *
 * NOTE: you should probably use timeAgoString when trying to display
 * an updated/created timestamp.
 *
 * @param {*} date - a Date object you want to convert into a string - gracefully handles any value
 * @param {object} options -
 * @param {string} options.timeZone - the timezone you want date displayed in, defaults to current users time zone
 * @returns {string} a formatted date string
 */
export const dateTimeString = (
  date: Date | number | string | null | undefined,
  { timeZone = defaultTimeZone, omitYear = false }: DateOnlyStringOptions = {},
): string => {
  if (isNil(date)) {
    return '';
  }

  const formattedDate = new Date(date);

  if (isInvalidDate(formattedDate)) {
    return '';
  }

  try {
    const baseOptions: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone,
    };

    const options = omitYear ? baseOptions : { ...baseOptions, year: 'numeric' };

    return formattedDate.toLocaleDateString(defaultLocales, options as Intl.DateTimeFormatOptions);
  } catch (error: unknown) {
    console.error(error); // eslint-disable-line no-console
    return ''; // handle bad input
  }
};
